import { extractColors, parseColorToRgba, shouldSkipColor } from "./svg-palette.js";

const imageManifestPath = "/assets/images-manifest.json";

const canvasFrame = document.querySelector("#canvas-frame");
const paletteRow = document.querySelector("#palette-row");
const continueHint = document.querySelector("#continue-hint");

const paintProps = ["fill", "stroke", "stop-color"];
const togglePaint = {
  fill: "#ffffff",
  stroke: "#d0d7df",
  "stop-color": "#ffffff",
};
const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const paintTargets = [];
let activeLetter = null;
let hasWon = false;
let letterToColor = new Map();
let imagePaths = [];


const colorsMatch = (first, second) => {
  if (!first || !second) return false;
  const channelMatch = (a, b) => Math.abs(a - b) <= 2;
  return (
    channelMatch(first.r, second.r) &&
    channelMatch(first.g, second.g) &&
    channelMatch(first.b, second.b) &&
    Math.abs(first.a - second.a) <= 0.05
  );
};


const shuffleArray = (items) => {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
};

const getReadableTextColor = (color) => {
  const parsed = parseColorToRgba(color);
  if (!parsed) return "#1d2b33";
  const luminance = (0.2126 * parsed.r + 0.7152 * parsed.g + 0.0722 * parsed.b) / 255;
  return luminance >= 0.6 ? "#1d2b33" : "#ffffff";
};

const assignLettersToColors = (colors) => {
  const letters = shuffleArray([...alphabet]);
  if (colors.length > letters.length) {
    console.warn("Palette has more than 26 colors; extra colors are skipped.");
  }
  const assignments = colors.slice(0, letters.length).map((color, index) => ({
    color,
    letter: letters[index],
  }));
  return assignments;
};

const renderPalette = (assignments) => {
  paletteRow.innerHTML = "";
  letterToColor = new Map();

  assignments.forEach(({ color, letter }) => {
    const dot = document.createElement("button");
    dot.className = "palette-dot";
    dot.style.background = color;
    dot.style.color = getReadableTextColor(color);
    dot.type = "button";
    dot.dataset.color = color;
    dot.dataset.letter = letter;
    dot.setAttribute("aria-label", `Color ${letter}`);
    dot.textContent = letter;
    paletteRow.appendChild(dot);
    letterToColor.set(letter, color);
  });
};

const clearActiveLetter = () => {
  activeLetter = null;
  paletteRow.querySelectorAll(".palette-dot").forEach((dot) => {
    dot.classList.remove("is-active");
  });
};

const setActiveLetter = (letter) => {
  activeLetter = letter;
  paletteRow.querySelectorAll(".palette-dot").forEach((dot) => {
    dot.classList.toggle("is-active", dot.dataset.letter === letter);
  });
};

const prepareSvg = () => {
  const svg = canvasFrame.querySelector("svg");
  if (!svg) return;
  paintTargets.length = 0;
  const nodes = svg.querySelectorAll("*");

  nodes.forEach((node) => {
    let paintable = false;
    paintProps.forEach((prop) => {
      const computedValue = window.getComputedStyle(node).getPropertyValue(prop).trim();
      if (shouldSkipColor(computedValue)) return;

      node.setAttribute(`data-paint-${prop}-original`, computedValue);
      node.style.setProperty(prop, togglePaint[prop]);
      paintable = true;
    });

    if (paintable) {
      node.setAttribute("data-paintable", "true");
      paintTargets.push(node);
    }
  });
};

const applyColorForLetter = (letter) => {
  const color = letterToColor.get(letter);
  if (!color) return;
  const targetRgba = parseColorToRgba(color);
  if (!targetRgba) return;

  paintTargets.forEach((node) => {
    paintProps.forEach((prop) => {
      const original = node.getAttribute(`data-paint-${prop}-original`);
      if (!original) return;
      const originalRgba = parseColorToRgba(original);
      if (!originalRgba || !colorsMatch(targetRgba, originalRgba)) return;
      node.style.setProperty(prop, original);
    });
  });
};

const isPainted = (node) => {
  return paintProps.every((prop) => {
    const original = node.getAttribute(`data-paint-${prop}-original`);
    if (!original) return true;
    const current = window.getComputedStyle(node).getPropertyValue(prop);
    const currentRgba = parseColorToRgba(current);
    const toggleRgba = parseColorToRgba(togglePaint[prop]);
    if (!currentRgba || !toggleRgba) return true;
    return !colorsMatch(currentRgba, toggleRgba);
  });
};

const handleSuccess = () => {
  if (hasWon) return;
  hasWon = true;
  document.body.classList.add("is-complete");
  console.info("Success! All colors filled.");
};

const checkForCompletion = () => {
  if (paintTargets.length === 0) return;
  const allPainted = paintTargets.every((node) => isPainted(node));
  if (allPainted) {
    handleSuccess();
  }
};

const handlePaletteClick = (event) => {
  const dot = event.target.closest(".palette-dot");
  if (!dot) return;
  const letter = dot.dataset.letter;
  if (!letter) return;
  setActiveLetter(letter);
  applyColorForLetter(letter);
  checkForCompletion();
};

const handleKeyDown = (event) => {
  if (event.code === "Space" || event.key === " ") {
    if (hasWon) {
      event.preventDefault();
      loadNextImage();
    }
    return;
  }
  if (event.key.length !== 1) return;
  const letter = event.key.toUpperCase();
  if (!letterToColor.has(letter)) return;
  setActiveLetter(letter);
  applyColorForLetter(letter);
  checkForCompletion();
};

paletteRow.addEventListener("click", handlePaletteClick);
continueHint.addEventListener("click", () => {
  if (!hasWon) return;
  loadNextImage();
});
window.addEventListener("keydown", handleKeyDown);

const buildShuffledQueue = () => {
  const queue = [...imagePaths];
  for (let i = queue.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [queue[i], queue[j]] = [queue[j], queue[i]];
  }
  return queue;
};

let imageQueue = [];
let imageIndex = 0;

const loadImagePaths = async () => {
  try {
    const response = await fetch(imageManifestPath);
    if (!response.ok) {
      throw new Error(`Manifest request failed (${response.status}).`);
    }

    const manifest = await response.json();
    if (!Array.isArray(manifest)) {
      throw new Error("Image manifest is not an array.");
    }

    return manifest.filter(
      (entry) => typeof entry === "string" && entry.toLowerCase().endsWith(".svg")
    );
  } catch (error) {
    console.error("Unable to load image manifest.", error);
    return [];
  }
};

const getNextImagePath = () => {
  if (imageIndex >= imageQueue.length) {
    imageQueue = buildShuffledQueue();
    imageIndex = 0;
  }
  const path = imageQueue[imageIndex];
  imageIndex += 1;
  return path;
};

const loadNextImage = async () => {
  if (imagePaths.length === 0) {
    canvasFrame.innerHTML = "";
    paletteRow.innerHTML = "";
    continueHint.hidden = true;
    return;
  }

  continueHint.hidden = false;
  const nextPath = getNextImagePath();
  const response = await fetch(nextPath);
  const svgText = await response.text();
  const colors = extractColors(svgText);
  canvasFrame.innerHTML = svgText;
  document.body.classList.remove("is-complete");
  hasWon = false;
  prepareSvg();
  const assignments = assignLettersToColors(colors);
  renderPalette(assignments);
  clearActiveLetter();
};

const initialize = async () => {
  imagePaths = await loadImagePaths();
  imageQueue = buildShuffledQueue();
  imageIndex = 0;
  await loadNextImage();
};

initialize();
